import { BUDGET, formatCost } from './draftRules.js';
import './BudgetBar.css';

/*
 * One bar carrying both figures.
 *
 * Spent and remaining are the same quantity read from opposite ends, so
 * they sit at opposite ends of a single track rather than in two tiles
 * that repeat the division.
 *
 * Going over is allowed while building — only publishing is blocked — so
 * this is the one place that says by how much, and it says it in words
 * inside the bar rather than leaving a red fill to imply it.
 *
 * A figure moves outside the track when its own segment gets too narrow to
 * hold it: under about a fifth of the width the text would overflow its
 * own fill and sit on the wrong colour.
 */

const INSIDE_THRESHOLD = 0.2;

export default function BudgetBar({ spent, remaining }) {
	const over = remaining < 0;
	const ratio = Math.min(1, Math.max(0, spent / BUDGET));
	const spentInside = ratio >= INSIDE_THRESHOLD;
	const remainingInside = !over && 1 - ratio >= INSIDE_THRESHOLD;

	return (
		<div className={`budget${over ? ' budget--over' : ''}`}>
			<div
				className="budget__track"
				role="meter"
				aria-valuenow={spent}
				aria-valuemin={0}
				aria-valuemax={BUDGET}
				aria-label={
					over
						? `${formatCost(spent)} spent, ${formatCost(-remaining)} over the ${formatCost(BUDGET)} budget`
						: `${formatCost(spent)} of ${formatCost(BUDGET)} spent`
				}
			>
				<div className="budget__fill" style={{ width: `${ratio * 100}%` }}>
					{spentInside && <span className="budget__figure">{formatCost(spent)}</span>}
					{/* Over budget fills the whole track, so the overage has room
					    to sit at the far end of it. */}
					{over && (
						<span className="budget__figure budget__figure--over">
							{formatCost(-remaining)} over
						</span>
					)}
				</div>
				{remainingInside && (
					<span className="budget__figure budget__figure--rest">
						{formatCost(remaining)} left
					</span>
				)}
			</div>

			{(!spentInside || (!remainingInside && !over)) && (
				<p className="budget__outside">
					{!spentInside && <span>{formatCost(spent)} spent</span>}
					{!remainingInside && !over && <span>{formatCost(remaining)} left</span>}
				</p>
			)}
		</div>
	);
}
